<script setup>
import WKT from "ol/format/WKT";
import { ref } from "vue";
import axios from "axios";

const props = defineProps({
    showWorksProject: {
        type: Boolean,
        required: true
    },
    selectedFeatures: {
        type: Array,
        required: true
    },
    users: {
        type: Array,
        required: true
    },
    permissions: {
        type: Object,
        required: true
    },
    polygonWkt: {
        type: String,
        required: true
    },
});

const emit = defineEmits(['update:showWorksProject']);

const step = ref(1);
const assignList = ref([]);
const loading = ref(false);
const removeExisting = ref(false);
const name = ref(null);
const notes = ref(null);

const next = () => {
    step.value++;
};

const closeModal = () => {
    emit('update:showWorksProject', false);
};

const createProject = () => {
    loading.value = true;
    const format = new WKT();

    let polygon = null;
    try {
        if (props.polygonWkt && props.polygonWkt.trim()) {
            const feature = format.readFeature(props.polygonWkt, {
                dataProjection: "EPSG:3857",
                featureProjection: "EPSG:4326",
            });
            polygon = format.writeGeometry(feature.getGeometry());
        }
    } catch (error) {
        console.error('Error processing WKT:', error);
    }

    axios
        .post("/api/works/project/create", {
            name: name.value,
            workTasks: null,
            notes: notes.value,
            totalTrees: props.selectedFeatures.length,
            treesToPlant: 0,
            typeSelected: 10,
            polygonArea: polygon,
            polygonAreaMeters: props.polygonWkt,
            canInspect: false,
            live: false,
            weeksComplete: null,
            plantingYears: null,
            plantingSpecies: null,
            filterString: null,
            worksIds: props.selectedFeatures,
        })
        .then((response) => {
            loading.value = false;
            window.location.href = "/projects";
        })
        .catch((error) => {
            alert("Error: " + error);
            closeModal();
        });
};
</script>

<template>
    <div
        style="max-height: 95%"
        class="fixed top-0 inset-x-0 px-4 py-4 sm:inset-0 sm:flex sm:items-center sm:justify-center z-10"
    >
        <div class="fixed inset-0 transition-opacity" style="z-index: -1">
            <div class="absolute inset-0 bg-slate-500 opacity-75"></div>
        </div>

        <div
            class="bg-white rounded-lg overflow-hidden shadow-xl transform transition-all sm:max-w-xl sm:w-full lg:w-1/2 z-10 overflow-y-auto"
            role="dialog"
            aria-modal="true"
            aria-labelledby="modal-headline"
        >
            <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div class="w-full">
                    <div v-if="step === 1" class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                        <h3 class="text-lg leading-6 font-medium text-slate-900" id="modal-headline">
                            Create Works Project
                        </h3>
                        <div class="mt-2">
                            <p class="leading-5 text-slate-600">
                                Create a Project containing for the
                                <strong>{{ selectedFeatures.length }}</strong>
                                Selected Works.
                            </p>
                        </div>
                        <div class="w-full md:flex md:items-center my-6">
                            <div class="md:w-1/3">
                                <label
                                    class="block text-slate-700 md:text-right mb-1 md:mb-0 pr-4"
                                    for="inline-full-name"
                                >
                                    Project Name
                                </label>
                            </div>
                            <div class="md:w-2/3">
                                <input
                                    class="bg-slate-100 appearance-none border border-slate-200 rounded-lg w-full py-2 px-4 text-slate-700 leading-tight focus:outline-none focus:bg-white focus:border-slate-400"
                                    id="inline-full-name"
                                    type="text"
                                    v-model="name"
                                />
                            </div>
                        </div>
                        <div class="w-full md:flex md:items-center mb-6">
                            <div class="md:w-1/3">
                                <label class="block text-slate-700 md:text-right mb-1 md:mb-0 pr-4" for="inline-notes">
                                    Project Notes
                                </label>
                            </div>
                            <div class="md:w-2/3">
                                <textarea
                                    class="bg-slate-100 appearance-none border border-slate-200 rounded-lg w-full py-2 px-4 text-slate-700 leading-tight focus:outline-none focus:bg-white focus:border-slate-400"
                                    id="inline-notes"
                                    rows="4"
                                    v-model="notes"
                                ></textarea>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="bg-slate-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <span class="mt-3 flex w-full rounded-md shadow-sm sm:mt-0 sm:w-auto">
                    <button
                        @click="closeModal()"
                        type="button"
                        class="mr-2 inline-flex justify-center w-full rounded-lg border border-slate-300 px-4 py-2 bg-slate-100 text-base leading-6 font-semibold text-slate-700 shadow-sm hover:text-slate-500 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue transition ease-in-out duration-150 sm:text-sm sm:leading-5"
                    >
                        Cancel
                    </button>
                    <button
                        v-if="!loading"
                        @click="createProject()"
                        type="button"
                        class="whitespace-nowrap inline-flex justify-center w-full rounded-lg border border-transparent px-4 py-2 bg-blue-600 text-base leading-6 font-semibold text-white shadow-sm hover:bg-blue-500 focus:outline-none focus:border-red-700 focus:shadow-outline-red transition ease-in-out duration-150 sm:text-sm sm:leading-5"
                    >
                        Create Project
                    </button>
                    <button
                        v-else
                        disabled
                        type="button"
                        class="whitespace-nowrap inline-flex justify-center w-full rounded-lg border border-transparent px-4 py-2 bg-blue-600 text-base leading-6 font-semibold text-white shadow-sm hover:bg-blue-500 focus:outline-none focus:border-red-700 focus:shadow-outline-red transition ease-in-out duration-150 sm:text-sm sm:leading-5"
                    >
                        Creating...
                    </button>
                </span>
            </div>
        </div>
    </div>
</template>


